import { useEffect, useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";

import styles from "./SelectPatientDialog.module.css";

// The autocomplete's dropdown has to clear the dialog it sits in.
const PATIENT_POPPER_Z_INDEX = 1400;

/**
 * Asks which patient an action is for, when the screen doesn't already know.
 *
 * The practice-wide Communications lists have no patient in context, so the actions
 * that write to a chart — add a letter, add a sick cert, send an SMS — had no subject
 * and were simply left out. This is the missing first step: pick the patient, then the
 * normal form opens exactly as it does on that patient's own page.
 *
 * Deliberately a separate step rather than a patient field inside each form. The forms
 * are the ones the patient page already uses, and threading an optional patient through
 * each would give three chances to get "which chart am I writing to" wrong.
 */
export const SelectPatientDialog = ({
  open,
  onClose,
  onSelect,
  title = "Select a patient",
  description,
  confirmLabel = "Continue",
}) => {
  const [selectedPatient, setSelectedPatient] = useState(null);

  // A dialog reopened after a previous pick must not arrive pre-filled with it.
  useEffect(() => {
    if (!open) setSelectedPatient(null);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} title={title} width={560}>
      <div className={styles.content}>
        {!!description && <p className={styles.description}>{description}</p>}

        <div className={styles.field}>
          <span className={styles.label}>Patient</span>
          <PatientAutocomplete
            value={selectedPatient}
            onChange={setSelectedPatient}
            popperZIndex={PATIENT_POPPER_Z_INDEX}
            placeholder="Search for the patient..."
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!selectedPatient?.id}
          onClick={() => onSelect(selectedPatient)}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
